/**
 * Primerry cart drawer UI helpers.
 * Native cart mutations remain owned by Horizon's cart/product form components.
 */
class PrimerryCartDrawerUi {
  constructor(root) {
    this.root = root;
    this.announcement = root.querySelector('[data-pcd-announcement]');
    this.announcementSlides = Array.from(root.querySelectorAll('[data-pcd-announcement-slide]'));
    this.announcementIndex = Math.max(0, this.announcementSlides.findIndex((slide) => slide.classList.contains('is-active')));
    this.announcementTimer = null;
    this.bind();
  }

  bind() {
    this.root.addEventListener('click', (event) => {
      const quickOpen = event.target.closest('[data-pcd-quick-open]');
      if (quickOpen) {
        event.preventDefault();
        this.openQuickView(quickOpen);
        return;
      }

      const previousAnnouncement = event.target.closest('[data-pcd-announcement-prev]');
      const nextAnnouncement = event.target.closest('[data-pcd-announcement-next]');
      if (previousAnnouncement) this.setAnnouncement(this.announcementIndex - 1);
      if (nextAnnouncement) this.setAnnouncement(this.announcementIndex + 1);

      const recPrevious = event.target.closest('[data-pcd-rec-prev]');
      const recNext = event.target.closest('[data-pcd-rec-next]');
      if (recPrevious || recNext) {
        const recommendations = (recPrevious || recNext).closest('[data-pcd-recommendations]');
        const track = recommendations?.querySelector('[data-pcd-rec-track]');
        const card = track?.querySelector('.pcd-recommendation');
        if (track && card) {
          const gap = Number.parseFloat(getComputedStyle(track).gap) || 8;
          track.scrollBy({ left: (card.getBoundingClientRect().width + gap) * (recNext ? 1 : -1), behavior: 'smooth' });
        }
      }
    });

    if (this.announcement?.dataset.mode === 'auto' && this.announcementSlides.length > 1) {
      const delay = Number.parseInt(this.announcement.dataset.autoplayMs || '5000', 10);
      this.announcementTimer = window.setInterval(() => this.setAnnouncement(this.announcementIndex + 1), Math.max(delay, 2000));
    }
  }

  async openQuickView(button) {
    const dialogId = button?.dataset?.pcdQuickOpen;
    if (!dialogId) return;

    const dialog = document.getElementById(dialogId);
    if (!(dialog instanceof HTMLDialogElement)) return;

    try {
      await customElements.whenDefined('theme-drawer');
      if (typeof this.root.close === 'function') await this.root.close();
    } catch (error) {
      console.warn('[primerry-cart-drawer] Unable to close cart drawer before quick view.', error);
    }

    if (!dialog.isConnected || dialog.open) return;
    dialog.showModal();
    document.documentElement.classList.add('pcd-cart-quick-view-open');
  }

  setAnnouncement(index) {
    if (!this.announcementSlides.length) return;
    this.announcementIndex = (index + this.announcementSlides.length) % this.announcementSlides.length;
    this.announcementSlides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === this.announcementIndex));
  }

  destroy() {
    if (this.announcementTimer) window.clearInterval(this.announcementTimer);
  }
}

const instances = new WeakMap();

function initPrimerryCartDrawer(scope = document) {
  scope.querySelectorAll?.('#cart-drawer').forEach((root) => {
    instances.get(root)?.destroy?.();
    instances.set(root, new PrimerryCartDrawerUi(root));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initPrimerryCartDrawer(), { once: true });
} else {
  initPrimerryCartDrawer();
}

// Horizon morphs the cart drawer after cart mutations. Re-initialize lightweight controls.
document.addEventListener('theme-drawer:open', (event) => {
  if (event.target?.id === 'cart-drawer') initPrimerryCartDrawer(document);
});
document.addEventListener('cart:refresh', () => requestAnimationFrame(() => initPrimerryCartDrawer(document)));



function updatePrimerryHeaderCartBubble(itemCount) {
  const count = Math.max(0, Number.parseInt(String(itemCount ?? 0), 10) || 0);
  const displayCount = Math.min(count, 99);

  document.querySelectorAll('.primerry-header__cart-count').forEach((bubble) => {
    bubble.textContent = String(displayCount);
    bubble.classList.toggle('primerry-header__cart-count--empty', count === 0);
  });

  // Horizon's native cart-icon keeps a short-lived count in sessionStorage.
  // Keep the custom Primerry header in sync with the same source of truth so
  // history restores / other cart UI cannot put the old count back.
  try {
    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(count),
        timestamp: Date.now(),
      })
    );
  } catch (_) {
    // Storage can be unavailable in strict/private browser contexts.
  }
}

function isPrimerryCartDrawerOpen(drawer = document.getElementById('cart-drawer')) {
  if (!drawer) return false;
  return Boolean(
    drawer.hasAttribute('open') ||
    drawer.isOpen === true ||
    drawer.querySelector?.('dialog[open]')
  );
}

async function restorePrimerryCartDrawerOpenState(shouldStayOpen, itemCount) {
  if (!shouldStayOpen || itemCount <= 0) return;

  // The native cart component morphs the cart section after the mutation
  // promise resolves. Waiting two frames ensures we work with the new drawer.
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

  const drawer = document.getElementById('cart-drawer');
  if (!drawer || isPrimerryCartDrawerOpen(drawer)) return;

  try {
    await customElements.whenDefined('theme-drawer');
    if (typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
  } catch (_) {
    // Fall through to the header trigger fallback.
  }

  document.querySelector('.primerry-header__cart')?.click();
}

// Horizon dispatches this event for native cart quantity +/- and remove actions.
// The Primerry header is custom markup (not Horizon's <cart-icon>), so sync its
// bubble explicitly and preserve the open drawer through the section morph.
if (!window.__primerryCartLinesSyncBound) {
  window.__primerryCartLinesSyncBound = true;

  document.addEventListener('shopify:cart:lines-update', (event) => {
    const shouldStayOpen = isPrimerryCartDrawerOpen();

    event.promise
      ?.then(({ cart, detail }) => {
        const rawCount = cart?.totalQuantity ?? detail?.itemCount;
        const itemCount = Math.max(0, Number.parseInt(String(rawCount ?? 0), 10) || 0);

        updatePrimerryHeaderCartBubble(itemCount);
        restorePrimerryCartDrawerOpenState(shouldStayOpen, itemCount);
        requestAnimationFrame(() => initPrimerryCartDrawer(document));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[primerry-cart-drawer] Cart line update failed.', error);
        }
      });
  });
}

function getCartQuickViewDialog(target) {
  if (!(target instanceof Element)) return null;
  const dialog = target.closest('[data-pdp-quick-dialog]');
  if (!(dialog instanceof HTMLDialogElement)) return null;
  return dialog.closest('[data-pcd-cart-quick-views]') ? dialog : null;
}

function syncQuickViewScrollLock() {
  const hasOpenCartQuickView = Boolean(document.querySelector('[data-pcd-cart-quick-views] [data-pdp-quick-dialog][open]'));
  document.documentElement.classList.toggle('pcd-cart-quick-view-open', hasOpenCartQuickView);
}

function closeCartQuickView(dialog) {
  if (dialog?.open) dialog.close();
  syncQuickViewScrollLock();
}

function initCartQuickViews(scope = document) {
  scope.querySelectorAll?.('[data-pcd-cart-quick-views] [data-pdp-quick-dialog]').forEach((dialog) => {
    if (dialog.dataset.pcdQuickViewBound === 'true') return;
    dialog.dataset.pcdQuickViewBound = 'true';
    dialog.addEventListener('close', syncQuickViewScrollLock);
  });
}

async function refreshCartDrawerSection() {
  const root = window.Shopify?.routes?.root || '/';
  const [cart, renderedSections] = await Promise.all([
    fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json()),
    fetch(`${window.location.pathname}?sections=cart-drawer-section`, {
      credentials: 'same-origin',
    }).then((response) => response.json()),
  ]);

  const drawerHtml = renderedSections?.['cart-drawer-section'];
  const currentSection = document.querySelector('#shopify-section-cart-drawer-section');

  if (drawerHtml && currentSection) {
    const template = document.createElement('template');
    template.innerHTML = drawerHtml.trim();
    const replacement = template.content.firstElementChild;
    if (replacement) currentSection.replaceWith(replacement);
  }

  updatePrimerryHeaderCartBubble(cart.item_count || 0);

  initPrimerryCartDrawer(document);
  initCartQuickViews(document);

  return cart;
}

async function handleCartQuickViewSubmit(form) {
  const button = form.querySelector('button[type="submit"]');
  const error = form.querySelector('[data-pdp-error]');
  if (!button || button.dataset.loading === 'true') return;

  const defaultLabel = button.dataset.defaultLabel || button.textContent.trim() || 'add to cart';
  const wasDisabled = button.disabled;

  button.dataset.loading = 'true';
  button.disabled = true;
  button.textContent = 'adding…';
  if (error) {
    error.hidden = true;
    error.textContent = '';
  }

  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
      credentials: 'same-origin',
    });
    const item = await response.json();
    if (!response.ok) throw new Error(item.description || item.message || 'Unable to add this product.');

    const dialog = form.closest('[data-pdp-quick-dialog]');
    closeCartQuickView(dialog);
    await refreshCartDrawerSection();

    requestAnimationFrame(async () => {
      try {
        await customElements.whenDefined('theme-drawer');
        const drawer = document.getElementById('cart-drawer');
        if (typeof drawer?.open === 'function') drawer.open();
        else document.querySelector('.primerry-header__cart')?.click();
      } catch {
        document.querySelector('.primerry-header__cart')?.click();
      }
    });
  } catch (submitError) {
    if (error) {
      error.textContent = submitError instanceof Error ? submitError.message : 'Unable to add this product.';
      error.hidden = false;
    }
  } finally {
    button.dataset.loading = 'false';
    button.disabled = wasDisabled;
    button.textContent = defaultLabel;
  }
}

if (!window.__primerryCartQuickViewBound) {
  window.__primerryCartQuickViewBound = true;

  document.addEventListener('click', (event) => {
    const dialog = getCartQuickViewDialog(event.target);
    if (!dialog) return;

    if (event.target === dialog) {
      closeCartQuickView(dialog);
      return;
    }

    const closeButton = event.target.closest('[data-pdp-quick-close]');
    if (closeButton) {
      event.preventDefault();
      closeCartQuickView(dialog);
      return;
    }

    const thumb = event.target.closest('[data-pdp-quick-thumb]');
    if (thumb) {
      event.preventDefault();
      const image = dialog.querySelector('.primerry-pdp-qv__main-image-element');
      if (image && thumb.dataset.imageSrc) {
        image.src = thumb.dataset.imageSrc;
        image.srcset = '';
        dialog.querySelectorAll('[data-pdp-quick-thumb]').forEach((item) => {
          item.classList.toggle('is-active', item === thumb);
        });
      }
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target instanceof Element
      ? event.target.closest('[data-pcd-cart-quick-views] [data-pdp-qv-form]')
      : null;
    if (!form) return;
    event.preventDefault();
    handleCartQuickViewSubmit(form);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCartQuickViews(), { once: true });
} else {
  initCartQuickViews();
}
